import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import {
  BillableIcon,
  CalenderIcon,
  CloseIcon,
  DateIcon,
  DeleteIcon,
  LeftArrowIcon,
  RightArrowIcon,
  TimeHourglassIcon,
} from '@sketchnote/icons';
import { Avatar, MoreActionMenu, Switch } from '@sketchnote/shared-components';
import { Button } from '@sketchnote/shared-components';
import cn from 'classnames';
import { formatISO, parseISO } from 'date-fns';
import * as React from 'react';
import { useState } from 'react';

import { DateRangePicker } from '../../tasks/components//DatePicker';
import { LogItem, LogUser, useTimesheetStore } from '../store/useTimesheetStore';
import { formatDateLight } from './Timesheet';

const TimeSubmitButtons = ({ onSave, onCancel }: { onSave: any; onCancel: () => void }) => {
  return (
    <div className="border-t py-12 flex justify-end">
      <Button onClick={onCancel} status="secondary" className="mr-16 min-w-100 flex justify-center">
        Cancel
      </Button>
      <Button onClick={onSave} status="primary" className="min-w-100 flex justify-center mr-12">
        Save
      </Button>
    </div>
  );
};

export const EditTimelog = ({
  logItem,
  logUser,
  onCloseModal,
  onDeleteLogEntry,
}: {
  logItem: LogItem;
  logUser: LogUser;
  onCloseModal: () => void;
  onDeleteLogEntry: () => Promise<void>;
}) => {
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState(false);
  const [isBillable, setIsBillable] = useState(logItem.billable);
  const [startDate, setStartDate] = useState<Date | undefined>(logItem?.startDate);
  const [hours, setHours] = useState<number | undefined>(Number(logItem.duration?.hours));
  const [minutes, setMinutes] = useState<number | undefined>(
    Number(logItem?.duration?.minutes) || 0
  );

  const MAX_HOUR_LIMIT = 10000;
  const { updateTimeLogEntry } = useTimesheetStore();

  const handleMenuItemClick = (e) => {
    if (e.value === 'delete') {
      setIsDeletePromptOpen(true);
    }
  };
  const handleHoursInput = (e) => {
    if (e.target.value < MAX_HOUR_LIMIT) setHours(e.target.value);
  };

  const handleMinutesInput = (e) => {
    if (e.target.value < 60) setMinutes(e.target.value);
  };

  const onSetStartDate = (dates) => {
    const dateValues = dates.length ? dates?.map((date) => formatISO(date)) : [formatISO(dates)];
    setStartDate(dateValues);
  };

  const handleUpdateEntry = async () => {
    await updateTimeLogEntry({
      logItemId: logItem?._id,
      billable: isBillable ?? false,
      duration: { hours: `${hours ?? 0}`, minutes: `${minutes ?? 0}` },
      startDate: startDate ?? new Date(),
    });
    onCloseModal();
  };

  return (
    <div>
      <div className="flex h-60 items-center justify-between shadow-sm px-12 py-6">
        <div className="flex items-center">
          {/* <div className="flex items-center justify-between">
            <div className="flex w-36 h-36 bg-blue-100 pr-16 rounded-md">
              <button onClick={handlePrevLog} className="cursor-pointer">
                <LeftArrowIcon wrapperClass="text-blue-500" width="14px" height="36px" />
              </button>
            </div>
            <div className="flex w-36 h-36 bg-blue-100 rounded-md pr-24">
              <button onClick={handleNextLog} className="cursor-pointer">
                <RightArrowIcon wrapperClass="text-blue-500" width="14px" height="36px" />
              </button>
            </div>
          </div> */}
          <div className="flex pr-8">
            <Avatar
              name={cn(logUser.firstname, logUser.lastname)}
              tooltip={false}
              type="photo"
              src={logUser.avatar}
              wrapperClassName="border-white border-2 transform transition duration-500 hover:scale-125"
            />
          </div>
          <div className="flex pr-12 text-gray-999 text-sm">{logUser.firstname}</div>
          <div className="flex pr-12 text-gray-999 text-sm font-medium">
            {logItem?.duration?.minutes
              ? `${logItem?.duration?.hours}h ${logItem?.duration?.minutes}m`
              : `${logItem?.duration?.hours} hours`}
          </div>
          <div className="flex pr-12 text-gray-999 text-sm">
            {formatDateLight([logItem?.startDate])}
          </div>
        </div>
        <div className="flex">
          <div className="self-end items-end pb-4 text-gray-400 border-gray-100 pr-12">
            <MoreActionMenu
              onClickMenuItem={handleMenuItemClick}
              classNames={{
                menuWrap: 'rounded-lg shadow-sm',
              }}
              menuItems={[
                {
                  label: (
                    <div className="flex font-regular text-gray-999 text-sm ">
                      <DeleteIcon wrapperClass="mt-4 text-gray-500" />
                      &nbsp;&nbsp; Delete
                    </div>
                  ),
                  value: 'delete',
                },
              ]}
            />
          </div>
          <button
            aria-label="Close button"
            className={cn({
              'text-gray-600 text-sm hover:bg-gray-100 py-5 rounded justify-end': true,
            })}
            onClick={onCloseModal}
          >
            <CloseIcon />
          </button>
        </div>
      </div>
      <div className="mx-12">
        <div className="text-sm text-gray-600 flex my-24 font-medium items-center justify-between">
          <div className="flex items-center">
            <BillableIcon className="mr-8" fill="#55585E" />
            <span className="mr-200">Billable</span>
          </div>
          <Switch checked={isBillable} onChange={() => setIsBillable(!isBillable)} />
        </div>
        <div>
          <label htmlFor="assignDate" className="text-base mb-8 block text-gray-500 font-medium'">
            Time entered
          </label>
        </div>
        <div className="flex mb-16">
          <div className="relative w-182 mr-12">
            <input
              id="totalEstimatedTime"
              type="number"
              min="0"
              max="10000"
              className=" bg-gray-100 pl-50 w-full placeholder-gray-400 rounded-sm h-48 border border-gray-200 text-left no-input-arrow"
              placeholder="Hours"
              autoComplete="off"
              onChange={handleHoursInput}
              value={hours}
            />
            <TimeHourglassIcon
              wrapperClass="text-gray-400 absolute p-5 top-12 left-10 left-12 border-2 border-gray-400 text-gray-400 border-dotted rounded-full"
              size={12}
            />
            <span className="absolute right-14 top-16 text-xs text-gray-500">hours</span>
          </div>
          <div className="relative w-182">
            <input
              id="totalEstimatedTime"
              type="number"
              min="0"
              max="60"
              className=" bg-gray-100 p-12 pl-50 w-full placeholder-gray-400 rounded-sm h-48 border border-gray-200 text-left no-input-arrow"
              placeholder="Minutes"
              autoComplete="off"
              onChange={handleMinutesInput}
              value={minutes}
            />
            <TimeHourglassIcon
              wrapperClass="text-gray-400 absolute p-5 top-12 left-10 left-12 border-2 border-gray-400 text-gray-400 border-dotted rounded-full"
              size={12}
            />
            <span className="absolute right-14 top-16 text-xs text-gray-500">mins</span>
          </div>
        </div>
        <div className="mb-16">
          <label htmlFor="assignDate" className="text-base mb-8 block text-gray-500 font-medium'">
            Selected date
          </label>
          <div className="relative bg-gray-100">
            <DateRangePicker
              value={[startDate ? parseISO(startDate.toString()) : new Date()]}
              onChange={onSetStartDate}
              placeholderText="Select date"
              initialTab="date"
              show={['date']}
              disableEndDate={true}
              inputClassName="datepicker p-12 pl-50 w-full rounded-sm h-48 border border-gray-200 text-left text-gray-400"
            />
            <CalenderIcon
              wrapperClass="text-gray-400 absolute p-5 top-8 left-10 left-12 text-gray-400"
              size={30}
            />
          </div>
        </div>

        {/* Label and description */}
        {/* <div className="mb-16">
        <label htmlFor="assignDate" className="text-base mb-8 block text-gray-500 font-medium'">
          Label
        </label>
        <div className="relative mb-16">
          <input
            id="label"
            type="text"
            className="p-12 w-full rounded-sm h-48 border border-gray-200 text-left"
            // autoComplete="off"
            onChange={handleChangeLabel}
          />
        </div>
      </div>
      <div className="mb-24">
        <label htmlFor="assignDate" className="text-base mb-8 block text-gray-500 font-medium'">
          Description
        </label>
        <div className="relative mb-16">
          <input
            id="label"
            type="textarea"
            placeholder="This is the description"
            className="p-12 w-full rounded-sm h-108 border border-gray-200 text-left"
            // autoComplete="off"
            onChange={handleChangeDesc}
          />
        </div>
      </div> */}
      </div>
      <TimeSubmitButtons onSave={handleUpdateEntry} onCancel={onCloseModal} />
      <Modal
        isOpen={isDeletePromptOpen}
        onClose={() => {
          setIsDeletePromptOpen(false);
        }}
      >
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsDeletePromptOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={onDeleteLogEntry}
            >
              Delete
            </button>
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
};
